/**
 * @fileoverview webStorageRefusal — marketing-shell's ONE reading of what a failed Web Storage
 * call means (SWALLOWED-CATCH-BURNDOWN-001 U3, 2026-10-09).
 *
 * A browser REFUSES Web Storage in exactly two ways (HTML Living Standard, "Web storage"):
 *
 *   - the `localStorage` getter throws a `SecurityError` DOMException when the user agent blocks
 *     storage for the page (site data disabled, an opaque origin);
 *   - `setItem` throws a `QuotaExceededError` DOMException when the value cannot be stored (the
 *     quota is full — Safari's legacy private mode has a quota of zero).
 *
 * Both are real, expected conditions for a marketing visitor. Anything else a storage call throws
 * is a defect and must be rethrown, never read as "no storage".
 *
 * ⚠️ page-shell carries the same reading in `page-shell/lib/esm/internal/webStorage.js`, which is
 * not exported. Converging the two needs a public home; that is recorded, not done here.
 *
 * @module @puffin-core/marketing-shell/internal/webStorageRefusal
 */

/** The two DOMException names a browser raises when it refuses Web Storage. */
const STORAGE_REFUSAL_NAMES = new Set(['SecurityError', 'QuotaExceededError']);

/**
 * Whether `error` is the browser refusing Web Storage — and nothing else.
 *
 * @param {*} error
 * @returns {boolean}
 */
export function isStorageRefusal(error) {
  return error instanceof DOMException && STORAGE_REFUSAL_NAMES.has(error.name);
}
